import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const moveCircle1 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(30px, -40px);
  }
  50% {
    transform: translate(-20px, -30px);
  }
  75% {
    transform: translate(40px, 20px);
  }
`;

const moveCircle2 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-40px, 20px);
  }
  50% {
    transform: translate(30px, 40px);
  }
  75% {
    transform: translate(-30px, -40px);
  }
`;

const moveCircle3 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(20px, 50px);
  }
  50% {
    transform: translate(-40px, 30px);
  }
  75% {
    transform: translate(40px, -20px);
  }
`;

const serviceOfferings = [
  {
    icon: "🤖",
    title: "Custom AI Agent Development",
    desc: "Build intelligent autonomous agents that learn, reason, and make decisions to automate complex business processes."
  },
  {
    icon: "🧠",
    title: "Machine Learning Solutions",
    desc: "Leverage ML models for predictive analytics, classification, and pattern recognition tailored to your business needs."
  },
  {
    icon: "🔗",
    title: "LLM Integration",
    desc: "Integrate large language models (GPT, Claude, Gemini) for natural language processing and intelligent conversational systems."
  },
  {
    icon: "📊",
    title: "Data Intelligence",
    desc: "Transform raw data into actionable insights with advanced analytics, data visualization, and reporting systems."
  },
  {
    icon: "⚙️",
    title: "AI Automation",
    desc: "Automate workflows and processes with AI-driven solutions that reduce manual work and increase efficiency."
  },
  {
    icon: "🚀",
    title: "AI Deployment & Scaling",
    desc: "Deploy AI models to production with monitoring, optimization, and continuous improvement infrastructure."
  }
];

const whyChooseUs = [
  {
    icon: "🧠",
    title: "AI Expertise",
    desc: "Deep expertise in machine learning, deep learning, and agentic AI with proven track record in delivering AI solutions."
  },
  {
    icon: "⚡",
    title: "Cutting-Edge Technology",
    desc: "Stay ahead with latest AI frameworks, models, and tools including transformers, LLMs, and autonomous agents."
  },
  {
    icon: "🎯",
    title: "Business-Focused Solutions",
    desc: "AI solutions designed to solve real business problems and deliver measurable ROI and competitive advantage."
  },
  {
    icon: "🔒",
    title: "Responsible AI",
    desc: "Ethical AI development with bias mitigation, transparency, and compliance with AI governance standards."
  }
];

const aiExpertise = [
  {
    icon: "🤖",
    title: "Agentic AI Systems",
    desc: "Autonomous agents that can plan, learn, and execute tasks with minimal human intervention."
  },
  {
    icon: "✨",
    title: "Generative AI",
    desc: "Leverage generative models for content creation, code generation, and creative applications."
  },
  {
    icon: "👁️",
    title: "Computer Vision",
    desc: "Image and video analysis for object detection, recognition, and visual understanding applications."
  },
  {
    icon: "💬",
    title: "Natural Language Processing",
    desc: "Advanced NLP for text analysis, sentiment analysis, summarization, and intelligent chatbots."
  },
  {
    icon: "📈",
    title: "Predictive Analytics",
    desc: "Forecasting models for demand planning, risk assessment, and data-driven decision making."
  }
];

const AIAgenticSolutions = () => {
  useDocumentTitle("Briozon - AI Agentic Solutions");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.15) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle1} 15s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, rgba(118, 75, 162, 0) 70%)",
            animation: `${moveCircle2} 18s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.1) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle3} 20s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />

        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  AI AGENTIC SOLUTIONS
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Intelligent{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    AI Agents
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Harness the power of autonomous AI agents to transform your business. Our agentic AI solutions enable intelligent automation, data-driven decision making, and next-generation customer experiences that drive competitive advantage.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🤖
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🧠</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Machine Learning
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🔗</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    LLM Integration
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>👁️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Computer Vision
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📊</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Analytics
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Advanced AI Capabilities & Services */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(160deg, #f4f7ff 0%, #ffffff 45%, #f9f5ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle dot-grid background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(91,124,250,0.07) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Section Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#5b7cfa",
                letterSpacing: "3px",
                display: "block",
                mb: 2,
              }}
            >
              ADVANCED AI CAPABILITIES
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "44px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              Advanced AI Capabilities &{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Services
              </Box>
            </Typography>
          </Box>

          {/* Feature Panel 01 — Intelligent AI Systems */}
          <Box
            sx={{
              mb: 4,
              borderRadius: "24px",
              background: "#fff",
              boxShadow: "0 6px 48px rgba(91, 124, 250, 0.09)",
              border: "1px solid rgba(91, 124, 250, 0.1)",
              overflow: "hidden",
            }}
          >
            {/* Gradient top bar */}
            <Box
              sx={{
                height: 5,
                background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)",
              }}
            />
            <Grid container>
              {/* Big number column */}
              <Grid
                item
                sm={2}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, rgba(91,124,250,0.05) 0%, rgba(118,75,162,0.05) 100%)",
                  borderRight: "1px solid rgba(91,124,250,0.08)",
                  minHeight: 300,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "88px",
                    fontWeight: 900,
                    color: "rgba(91,124,250,0.13)",
                    lineHeight: 1,
                    userSelect: "none",
                    letterSpacing: "-4px",
                  }}
                >
                  01
                </Typography>
              </Grid>

              {/* Main content */}
              <Grid item xs={12} sm={7} sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      background:
                        "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      boxShadow: "0 8px 24px rgba(91,124,250,0.3)",
                      flexShrink: 0,
                    }}
                  >
                    🤖
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#1b1f33",
                      fontSize: { xs: "20px", sm: "23px", md: "26px" },
                      lineHeight: 1.25,
                    }}
                  >
                    Intelligent AI Systems
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.9,
                    fontSize: { xs: "15px", sm: "17px", md: "18px" },
                    textAlign: "justify",
                  }}
                >
                  We develop cutting-edge agentic AI systems that think, learn, and act
                  autonomously. Our AI solutions leverage machine learning, deep learning,
                  and large language models to solve complex business problems. From
                  predictive analytics to autonomous agents, we create intelligent systems
                  that drive innovation and efficiency.
                </Typography>
              </Grid>

              {/* Tech badge column */}
              <Grid
                item
                xs={12}
                sm={3}
                sx={{
                  p: { xs: 3, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 1.5,
                  borderLeft: { sm: "1px solid rgba(91,124,250,0.08)" },
                  background: {
                    sm: "linear-gradient(135deg, rgba(91,124,250,0.03) 0%, rgba(118,75,162,0.03) 100%)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#5b7cfa",
                    fontSize: "11px",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Technologies
                </Typography>
                {[
                  { label: "LLM", icon: "🧠" },
                  { label: "NLP", icon: "💬" },
                  { label: "Computer Vision", icon: "👁️" },
                ].map((tech) => (
                  <Box
                    key={tech.label}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      background: "rgba(91,124,250,0.06)",
                      border: "1px solid rgba(91,124,250,0.15)",
                      color: "#1b1f33",
                      fontWeight: 600,
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "all 0.3s ease",
                      cursor: "default",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                        color: "#fff",
                        transform: "translateX(6px)",
                        boxShadow: "0 6px 20px rgba(91,124,250,0.3)",
                        border: "1px solid transparent",
                      },
                    }}
                  >
                    <span>{tech.icon}</span>
                    {tech.label}
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Box>

          {/* Feature Panel 02 — Multi-Modal AI Capabilities */}
          <Box
            sx={{
              mb: 6,
              borderRadius: "24px",
              background: "#fff",
              boxShadow: "0 6px 48px rgba(118, 75, 162, 0.09)",
              border: "1px solid rgba(118, 75, 162, 0.1)",
              overflow: "hidden",
            }}
          >
            {/* Gradient top bar (reversed) */}
            <Box
              sx={{
                height: 5,
                background: "linear-gradient(90deg, #764ba2 0%, #5b7cfa 100%)",
              }}
            />
            <Grid container>
              {/* Tech badge column — left side this time */}
              <Grid
                item
                xs={12}
                sm={3}
                sx={{
                  p: { xs: 3, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 1.5,
                  borderRight: { sm: "1px solid rgba(118,75,162,0.08)" },
                  background: {
                    sm: "linear-gradient(135deg, rgba(118,75,162,0.03) 0%, rgba(91,124,250,0.03) 100%)",
                  },
                  order: { xs: 2, sm: 1 },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#764ba2",
                    fontSize: "11px",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Technologies
                </Typography>
                {[
                  { label: "ML / DL", icon: "📊" },
                  { label: "Gen AI", icon: "✨" },
                  { label: "Autonomous Agents", icon: "🤖" },
                ].map((tech) => (
                  <Box
                    key={tech.label}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      background: "rgba(118,75,162,0.06)",
                      border: "1px solid rgba(118,75,162,0.15)",
                      color: "#1b1f33",
                      fontWeight: 600,
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "all 0.3s ease",
                      cursor: "default",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)",
                        color: "#fff",
                        transform: "translateX(-6px)",
                        boxShadow: "0 6px 20px rgba(118,75,162,0.3)",
                        border: "1px solid transparent",
                      },
                    }}
                  >
                    <span>{tech.icon}</span>
                    {tech.label}
                  </Box>
                ))}
              </Grid>

              {/* Main content */}
              <Grid
                item
                xs={12}
                sm={7}
                sx={{ p: { xs: 3, sm: 4, md: 5 }, order: { xs: 1, sm: 2 } }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      background:
                        "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      boxShadow: "0 8px 24px rgba(118,75,162,0.3)",
                      flexShrink: 0,
                    }}
                  >
                    🚀
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#1b1f33",
                      fontSize: { xs: "20px", sm: "23px", md: "26px" },
                      lineHeight: 1.25,
                    }}
                  >
                    Multi-Modal AI Capabilities
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.9,
                    fontSize: { xs: "15px", sm: "17px", md: "18px" },
                    textAlign: "justify",
                  }}
                >
                  Our AI agents can be deployed across your entire organization to handle
                  customer service, data processing, decision-making, and process
                  automation. With multi-modal AI capabilities including NLP, computer
                  vision, and generative AI, we transform how your business operates and
                  scales intelligent operations.
                </Typography>
              </Grid>

              {/* Big number column */}
              <Grid
                item
                sm={2}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, rgba(118,75,162,0.05) 0%, rgba(91,124,250,0.05) 100%)",
                  borderLeft: "1px solid rgba(118,75,162,0.08)",
                  order: { sm: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "88px",
                    fontWeight: 900,
                    color: "rgba(118,75,162,0.13)",
                    lineHeight: 1,
                    userSelect: "none",
                    letterSpacing: "-4px",
                  }}
                >
                  02
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Bottom — Expertise Showcase */}

          {/* Divider chip */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 5 }}>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent, rgba(91,124,250,0.3))",
              }}
            />
            <Box
              sx={{
                px: 3,
                py: 1,
                borderRadius: "50px",
                background:
                  "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                boxShadow: "0 4px 18px rgba(91,124,250,0.35)",
                whiteSpace: "nowrap",
              }}
            >
              Our Expertise & Capabilities
            </Box>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                background:
                  "linear-gradient(90deg, rgba(118,75,162,0.3), transparent)",
              }}
            />
          </Box>

          {/* 3 AI Pillar Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              {
                icon: "🧠",
                title: "Machine Learning",
                grad: "135deg, #5b7cfa 0%, #764ba2 100%",
                shadow: "91,124,250",
                desc: "Advanced ML & deep learning models that extract patterns from your data to predict outcomes, classify information, and drive smarter business decisions at scale.",
              },
              {
                icon: "✨",
                title: "Generative AI",
                grad: "135deg, #9b59b6 0%, #5b7cfa 100%",
                shadow: "155,89,182",
                desc: "State-of-the-art GenAI systems that generate content, code, and automation workflows — transforming how your teams create, collaborate, and operate.",
              },
              {
                icon: "🤖",
                title: "Autonomous Agents",
                grad: "135deg, #764ba2 0%, #5b7cfa 100%",
                shadow: "118,75,162",
                desc: "Self-operating AI agents deployed across your organization to handle customer service, data processing, and complex decision-making with zero human hand-holding.",
              },
            ].map((pillar) => (
              <Grid
                item
                xs={12}
                sm={4}
                key={pillar.title}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: { xs: 3, sm: 3.5 },
                    borderRadius: "20px",
                    background: "#fff",
                    boxShadow: `0 4px 32px rgba(${pillar.shadow},0.08)`,
                    border: `1px solid rgba(${pillar.shadow},0.13)`,
                    textAlign: "center",
                    transition: "all 0.35s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 56px rgba(${pillar.shadow},0.18)`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: "20px",
                      background: `linear-gradient(${pillar.grad})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "32px",
                      mx: "auto",
                      mb: 2.5,
                      boxShadow: `0 10px 28px rgba(${pillar.shadow},0.28)`,
                    }}
                  >
                    {pillar.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "17px", sm: "18px" },
                      color: "#1b1f33",
                      mb: 1.5,
                    }}
                  >
                    {pillar.title}
                  </Typography>
                  <Typography
                    sx={{ color: "#4c5472", fontSize: "15px", lineHeight: 1.8 }}
                  >
                    {pillar.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* 4 Capability Feature Rows */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[
              {
                icon: "🎯",
                label: "Context-Aware Intelligence",
                desc: "AI systems that read your business context and adapt reasoning in real-time for precise, relevant outputs",
              },
              {
                icon: "📈",
                label: "Continuous Learning",
                desc: "Models that evolve and sharpen with every interaction, feedback loop, and incoming dataset",
              },
              {
                icon: "⚡",
                label: "Scalable & Reliable",
                desc: "Enterprise-grade architecture designed for high availability, scalability, and measurable ROI",
              },
              {
                icon: "🛡️",
                label: "Responsible AI",
                desc: "Ethical, transparent, and auditable AI built to meet compliance standards and earn user trust",
              },
            ].map((cap) => (
              <Grid item xs={12} sm={6} key={cap.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.75)",
                    border: "1px solid rgba(91,124,250,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#fff",
                      boxShadow: "0 6px 28px rgba(91,124,250,0.1)",
                      border: "1px solid rgba(91,124,250,0.22)",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "13px",
                      flexShrink: 0,
                      background:
                        "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                      border: "1px solid rgba(91,124,250,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "22px",
                    }}
                  >
                    {cap.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "15px",
                        color: "#1b1f33",
                        mb: 0.5,
                      }}
                    >
                      {cap.label}
                    </Typography>
                    <Typography
                      sx={{ color: "#6b7490", fontSize: "14px", lineHeight: 1.7 }}
                    >
                      {cap.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Quote Banner */}
          <Box
            sx={{
              p: { xs: 4, sm: 5, md: 6 },
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #eef1ff 0%, #f5f0ff 50%, #eef1ff 100%)",
              border: "1px solid rgba(91,124,250,0.15)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-30%",
                left: "-5%",
                width: 240,
                height: 240,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(91,124,250,0.12) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "-30%",
                right: "-5%",
                width: 240,
                height: 240,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(118,75,162,0.12) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "17px", sm: "21px", md: "25px" },
                fontWeight: 700,
                color: "#1b1f33",
                lineHeight: 1.7,
                position: "relative",
                zIndex: 1,
                maxWidth: 820,
                mx: "auto",
              }}
            >
              Whether you need{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                intelligent automation
              </Box>{" "}
              or{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                data-driven insights
              </Box>{" "}
              — we deliver responsible, ethical AI solutions that evolve, predict, and
              transform your business outcomes.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* AI Capabilities */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 3,
              }}
            >
              Our AI & ML{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Capabilities
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                textAlign: "center",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              Comprehensive AI capabilities to solve diverse business challenges and opportunities.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {aiExpertise.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid rgba(91, 124, 250, 0.1)",
                    backgroundColor: "#fafbff",
                    transition: "all 0.3s ease",
                    minHeight: 220,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    boxSizing: "border-box",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 40px rgba(91, 124, 250, 0.12)",
                      borderColor: "rgba(91, 124, 250, 0.3)",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <Box sx={{ fontSize: "2rem", mb: 1.5 }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.7 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fafbff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 3,
              }}
            >
              Why Choose Briozon for{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                AI Solutions
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid rgba(91, 124, 250, 0.1)",
                    backgroundColor: "#fff",
                    transition: "all 0.3s ease",
                    textAlign: "center",
                    minHeight: 260,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    boxSizing: "border-box",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 40px rgba(91, 124, 250, 0.12)",
                      borderColor: "rgba(91, 124, 250, 0.3)",
                    },
                  }}
                >
                  <Box sx={{ fontSize: "2.5rem", mb: 2 }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 1.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.7 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Service Offerings Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 3,
              }}
            >
              Our AI & Agentic{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Services
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                textAlign: "center",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              End-to-end AI development from strategy and architecture to deployment and continuous optimization.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {serviceOfferings.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid rgba(91, 124, 250, 0.1)",
                    backgroundColor: "#fafbff",
                    transition: "all 0.3s ease",
                    minHeight: 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 40px rgba(91, 124, 250, 0.12)",
                      borderColor: "rgba(91, 124, 250, 0.3)",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.6rem",
                      mb: 2,
                      boxShadow: "0 8px 20px rgba(91, 124, 250, 0.25)",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1b1f33",
                      mb: 1,
                      fontSize: { xs: "20px", sm: "22px" },
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "16px", sm: "17px" },
                      color: "#4c5472",
                      lineHeight: 1.7,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Ready to Transform with AI?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Discover how AI and agentic solutions can revolutionize your business operations. Schedule a consultation with our AI experts to explore intelligent automation, predictive analytics, and autonomous systems tailored to your needs.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: "16px",
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AIAgenticSolutions;
